import { useNavigate, useParams } from "react-router-dom";
import { useEffect, useState, useCallback, useMemo, useRef } from "react";
import { generateSmile, resetSmileState, setRemainingCredits } from "../../store/smileDesign/smileGenerationSlice";
import { useAppDispatch, useAppSelector } from "../../store/hooks";
import { fetchActivePrompts } from "../../store/smileDesign/smilePromptSlice";
import FullscreenViewer from "../../components/smileDesign/FullscreenViewer";
import { Helmet } from "react-helmet-async";
import BeforeAfterPreview from "../../components/smileDesign/BeforeAfterPreview";
import ImageInput from "../../components/smileDesign/ImageInput";
import ActionButton from "../../components/smileDesign/GenerationPage/ActionButton";
import { FormCard } from "../../components/smileDesign/GenerationPage/FormCard";
import ImageFlowStep from "../../components/smileDesign/GenerationPage/ImageFlowStep";
import HistorySection from "../../components/smileDesign/HistorySection";
import { SmileSelector } from "../../components/smileDesign/GenerationPage/SmileSelector";
import { createSmileCase, fetchFullCaseBySmileId, syncSmileImages } from "../../store/smileDesign/smileCasesSlice";
import GenerationLoader from "../../components/smileDesign/GenerationPage/GenerationLoader";
import CreateCaseModal from "../../components/smileDesign/GenerationPage/CreateCaseModal";
import { uploadSmileCaseFiles } from "../../components/smileDesign/GenerationPage/UploadFiles";
import { uploadSmileVideoToCase } from "../../components/smileDesign/GenerationPage/saveSmileVideo";
import { fetchBoxAccessToken, fetchUser } from "../../store/common/authSlice";
import { getBoxFile, getBoxPreviewUrl } from "../../utils/smileThumbnail";
import { useSmileUnsavedChanges } from "../../context/smileUnsavedChangesContext";
import { FiAlertCircle, FiChevronRight, FiSave } from "react-icons/fi";
import WorkflowStageStepper, {
    type WorkflowStage,
} from "../../components/smileDesign/GenerationPage/WorkflowStageStepper";
import VideoGenerationStage, {
    UpcomingVideoStage,
} from "../../components/smileDesign/GenerationPage/VideoGenerationStage";
import DentalAccuracyStage from "../../components/smileDesign/GenerationPage/DentalAccuracyStage";
import SmileCaseOrderSection from "../../components/smileDesign/GenerationPage/SmileCaseOrderSection";
import type { CreatedPortalCase } from "../../components/uploader/caseDetailsFormNew";
import UnsavedStageConfirmModal from "../../components/smileDesign/GenerationPage/UnsavedStageConfirmModal";
import { markVideoSaved, resetVideoState, setCaseVideo } from "../../store/smileDesign/videoGenerationSlice";
import {
    afterImageNameFromOverlayName,
    afterImageNameFromRestoredName,
    afterImageNameFromWireframeName,
    afterSmileTypeFromOverlayFileType,
    afterSmileTypeFromRestoredFileType,
    afterSmileTypeFromWireframeFileType,
    isAfterDesignItem,
    isEngineFileType,
    isOverlayFileType,
    isRestoredFileType,
    isSmileVideoFileType,
    isWireframeFileType,
    overlayImageNameForAfter,
    restoredImageNameForAfter,
    wireframeImageNameForAfter,
    type HistoryItem,
} from "../../types/smileDesign/historyItem";
import {
    DENTAL_ACCURACY_EXTEND_LABEL,
    DENTAL_ACCURACY_OUTLINE_LABEL,
    DENTAL_ACCURACY_REFINE_LABEL,
} from "../../utils/smileDesign/dentalAccuracyOutlinePrompt";

function attachmentUnsaved(
    item: HistoryItem
): boolean {
    return (
        (!!item.wireframe && !item.wireframe.saved) ||
        (!!item.overlay && !item.overlay.saved) ||
        (!!item.restored && !item.restored.saved)
    );
}

function saveErrorMessage(error: unknown, fallback: string): string {
    if (typeof error === "string" && error.trim()) return error;
    if (error instanceof Error && error.message.trim()) return error.message;
    return fallback;
}

function imageMimeFromName(name: string, fallback = "image/jpeg"): string {
    const ext = name.split(".").pop()?.toLowerCase();
    switch (ext) {
        case "png":
            return "image/png";
        case "webp":
            return "image/webp";
        case "gif":
            return "image/gif";
        case "avif":
            return "image/avif";
        case "jpg":
        case "jpeg":
            return "image/jpeg";
        default:
            return fallback;
    }
}

/** Box / blob URLs often report application/octet-stream — force a real image MIME. */
function ensureImageMime(type: string | undefined, filename: string): string {
    if (type?.startsWith("image/")) return type;
    return imageMimeFromName(filename);
}

export default function SmileGeneration() {
    const dispatch = useAppDispatch();
    const navigate = useNavigate();
    const { setHasUnsavedChanges, registerSaveHandler } = useSmileUnsavedChanges();

    const { user, boxAccessToken } = useAppSelector((state) => state.auth);
    const { activePrompts } = useAppSelector((state) => state.smilePrompt);
    const {
        loading,
        result,
        error,
        remainingCredits,
    } = useAppSelector((state) => state.smileGeneration);
    const { selectedCase, isFetchingCase } = useAppSelector((state) => state.smileCases);
    const videoReady = useAppSelector(
        (s) => !!s.videoGeneration.videoUrl || !!s.videoGeneration.boxFileId
    );
    const {
        videoUrl: caseVideoUrl,
        saved: videoSaved,
    } = useAppSelector((s) => s.videoGeneration);

    const [showCreditsModal, setShowCreditsModal] = useState(false);

    const { smile_id } = useParams();
    const isNewCase = !smile_id;

    const [fullscreenMode, setFullscreenMode] = useState(false);
    const [patientName, setPatientName] = useState("");
    const [labToken, setLabToken] = useState("");
    const [createCaseError, setCreateCaseError] = useState<string | null>(null);
    const [customSmile, setCustomSmile] = useState("");
    const [useSelectPrompt] = useState(true);
    const [file, setFile] = useState<File | null>(null);

    const [preview, setPreview] = useState<string | null>(null);
    const [history, setHistory] = useState<HistoryItem[]>([]);
    const [selectedImage, setSelectedImage] = useState<HistoryItem | null>(null);
    /** After image URL that Stage 2 outline generation should attach to */
    const pendingWireframeSourceRef = useRef<string | null>(null);
    /** After image URL that Stage 2 extend generation should attach to */
    const pendingExtendSourceRef = useRef<string | null>(null);
    const uploadStepRef = useRef<HTMLElement>(null);

    const [baseSmileId, setBaseSmileId] = useState("");

    const [creatingCase, setCreatingCase] = useState(false);
    const [savingCase, setSavingCase] = useState(false);
    const [inputSaved, setInputSaved] = useState(false);
    const [showCaseModal, setShowCaseModal] = useState(isNewCase);

    const [currentStage, setCurrentStage] = useState<WorkflowStage>(1);
    const [portalCase, setPortalCase] = useState<CreatedPortalCase | null>(null);
    const [liveEditedAfter, setLiveEditedAfter] = useState<string | null>(null);
    const handleLiveEditedAfter = useCallback((url: string | null) => {
        setLiveEditedAfter(url);
    }, []);
    const [pendingStage, setPendingStage] = useState<WorkflowStage | null>(null);
    const [saveError, setSaveError] = useState<string | null>(null);

    // --- PRE-EXISTING DATA LOADING STATE ---
    const [isDataInitializing, setIsDataInitializing] = useState(false);

    const [smileCase, setSmileCase] = useState<{
        smile_id: string;
        patient_name: string;
        caseBoxId: number;
    } | null>(null);

    const hasUnsavedHistory = history.some(
        (item) =>
            isAfterDesignItem(item) &&
            (!item.saved || attachmentUnsaved(item))
    );
    const isSuperAdmin = user?.role === "Super Admin";
    // Clients need both portals. Admin-side roles already have every portal.
    const canOrderCaseFromSmile =
        !!user &&
        (user.role !== "User" || (!!user.verified && !!user.verifySmile));
    const credits =
        typeof remainingCredits === "number"
            ? remainingCredits
            : user?.smile_credits;

    const canSave =
        !!file &&
        (!inputSaved || hasUnsavedHistory || (videoReady && !videoSaved));

    const hasUnsavedWireframes = history.some(
        (item) =>
            isAfterDesignItem(item) &&
            ((item.wireframe && !item.wireframe.saved) ||
                (item.overlay && !item.overlay.saved) ||
                (item.restored && !item.restored.saved))
    );

    const basePrompts = useMemo(
        () =>
            (Array.isArray(activePrompts) ? activePrompts : []).filter(
                (p) => p.prompt_type === "base" && p.active !== false
            ),
        [activePrompts]
    );

    const hasUnsavedChanges = useMemo(() => {
        const hasUnsaved =
            history.some(
                (item) =>
                    isAfterDesignItem(item) &&
                    (!item.saved || attachmentUnsaved(item))
            );
        const hasUnsavedVideo = videoReady && !videoSaved;
        if (!file && history.length === 0 && !hasUnsavedVideo) return false;
        return !inputSaved || hasUnsaved || hasUnsavedVideo;
    }, [inputSaved, history, file, videoReady, videoSaved]);

    useEffect(() => {
        setPortalCase(null);
        dispatch(fetchBoxAccessToken());
        dispatch(resetSmileState());
        dispatch(resetVideoState());

        // Drop the previous case's photo/history immediately so a late
        // generate or Box load cannot show patient B on patient A's case.
        setFile(null);
        setPreview(null);
        setHistory([]);
        setSelectedImage(null);
        setLiveEditedAfter(null);
        pendingWireframeSourceRef.current = null;
        pendingExtendSourceRef.current = null;
        setSaveError(null);

        if (!isNewCase && smile_id) {
            setSmileCase(null);
            setIsDataInitializing(true);
            setShowCaseModal(false);
            dispatch(fetchFullCaseBySmileId(smile_id));
        } else {
            setSmileCase(null);
            setPatientName("");
            setLabToken("");
            setCreateCaseError(null);
            setCustomSmile("");
            setBaseSmileId("");
            setInputSaved(false);
            setShowCaseModal(true);
            setCurrentStage(1);
            setIsDataInitializing(false);
        }

        return () => {
            dispatch(resetSmileState());
            dispatch(resetVideoState());
        };
    }, [dispatch, smile_id, isNewCase]);

    useEffect(() => {
        if (
            error ===
            "Your smile credits have been exhausted. Please purchase additional credits to continue using this feature."
        ) {
            setShowCreditsModal(true);
        }
    }, [error]);

    useEffect(() => {
        if (useSelectPrompt) {
            dispatch(fetchActivePrompts());
        }
    }, [dispatch, useSelectPrompt]);

    useEffect(() => {
        if (!baseSmileId) return;
        const stillAvailable = basePrompts.some(
            (p) => String(p.id) === String(baseSmileId)
        );
        if (!stillAvailable) setBaseSmileId("");
    }, [baseSmileId, basePrompts]);

    // --- REFACTORED WORKFLOW CASE INITIALIZER ---
    useEffect(() => {
        let cancelled = false;

        const loadCaseData = async () => {
            if (isNewCase) {
                setIsDataInitializing(false);
                return;
            }
            if (!selectedCase || !boxAccessToken) return;
            if (selectedCase.caseDetails.smile_id !== smile_id) return;

            try {
                setIsDataInitializing(true);

                const { caseDetails, caseFiles = [] } = selectedCase;

                setSmileCase({
                    smile_id: caseDetails.smile_id,
                    patient_name: caseDetails.patient_name,
                    caseBoxId: caseDetails.box_folder_id || 0,
                });
                setPatientName(caseDetails.patient_name);
                setShowCaseModal(false);
                if (caseDetails.case_id) {
                    setPortalCase({
                        case_id: caseDetails.case_id,
                        case_owner: caseDetails.patient_name || "",
                        box_folder_id: null,
                    });
                }

                const inputFile = caseFiles.find((f: any) => f.file_type === "Input");
                const generatedFiles = caseFiles.filter((f: any) => f.file_type !== "Input");
                const videoFiles = generatedFiles.filter((f: any) =>
                    isSmileVideoFileType(f.file_type)
                );
                const afterFiles = generatedFiles.filter(
                    (f: any) =>
                        !isWireframeFileType(f.file_type) &&
                        !isOverlayFileType(f.file_type) &&
                        !isRestoredFileType(f.file_type) &&
                        !isSmileVideoFileType(f.file_type) &&
                        !isEngineFileType(f.file_type)
                );
                const wireframeFiles = generatedFiles.filter((f: any) =>
                    isWireframeFileType(f.file_type)
                );
                const overlayFiles = generatedFiles.filter((f: any) =>
                    isOverlayFileType(f.file_type)
                );
                const restoredFiles = generatedFiles.filter((f: any) =>
                    isRestoredFileType(f.file_type)
                );

                // Only mark input as saved when an Input file actually exists.
                // Otherwise Save skips uploading the before image (uploadInput: false).
                setInputSaved(!!inputFile);

                if (inputFile) {
                    try {
                        const url = await getBoxPreviewUrl(
                            inputFile.file_id,
                            boxAccessToken,
                            inputFile.file_name
                        );
                        if (cancelled) return;
                        if (url) setPreview(url);

                        const blob = await getBoxFile(inputFile.file_id, boxAccessToken);
                        if (cancelled) return;
                        const existingFile = new File([blob], inputFile.file_name, {
                            type: ensureImageMime(blob.type, inputFile.file_name),
                        });
                        setFile(existingFile);
                    } catch (err) {
                        console.error("Preview/File asset payload load failed", err);
                    }
                }

                const loadThumbnailsInChunks = async (items: any[]) => {
                    const results: HistoryItem[] = [];
                    const chunkSize = 4;

                    for (let i = 0; i < items.length; i += chunkSize) {
                        const chunk = items.slice(i, i + chunkSize);

                        const chunkRes = await Promise.all(
                            chunk.map(async (fileItem): Promise<HistoryItem | null> => {
                                try {
                                    const imageUrl = await getBoxPreviewUrl(
                                        fileItem.file_id,
                                        boxAccessToken,
                                        fileItem.file_name
                                    );
                                    return {
                                        image: imageUrl,
                                        smileType: fileItem.file_type,
                                        imageName: fileItem.file_name,
                                        saved: true,
                                    };
                                } catch (e) {
                                    return null;
                                }
                            })
                        );

                        const validChunkItems = chunkRes.filter((item): item is HistoryItem => item !== null);
                        results.push(...validChunkItems);
                    }
                    return results;
                };

                const [afterHistory, wireframeHistory, overlayHistory, restoredHistory] =
                    await Promise.all([
                        loadThumbnailsInChunks(afterFiles),
                        loadThumbnailsInChunks(wireframeFiles),
                        loadThumbnailsInChunks(overlayFiles),
                        loadThumbnailsInChunks(restoredFiles),
                    ]);
                if (cancelled) return;

                const validHistory = afterHistory.map((after) => {
                    const matchedWf =
                        wireframeHistory.find((wf) => {
                            const expectedAfterName = afterImageNameFromWireframeName(
                                wf.imageName
                            );
                            if (expectedAfterName && expectedAfterName === after.imageName) {
                                return true;
                            }
                            const parentType = afterSmileTypeFromWireframeFileType(
                                wf.smileType
                            );
                            return (
                                parentType === after.smileType &&
                                wireframeImageNameForAfter(after.imageName) ===
                                    wf.imageName
                            );
                        }) ?? null;

                    const matchedOverlay =
                        overlayHistory.find((ov) => {
                            const expectedAfterName = afterImageNameFromOverlayName(
                                ov.imageName
                            );
                            if (
                                expectedAfterName &&
                                expectedAfterName === after.imageName
                            ) {
                                return true;
                            }
                            const parentType = afterSmileTypeFromOverlayFileType(
                                ov.smileType
                            );
                            return (
                                parentType === after.smileType &&
                                overlayImageNameForAfter(after.imageName) ===
                                    ov.imageName
                            );
                        }) ?? null;

                    const matchedRestored =
                        restoredHistory.find((rs) => {
                            const expectedAfterName = afterImageNameFromRestoredName(
                                rs.imageName
                            );
                            if (
                                expectedAfterName &&
                                expectedAfterName === after.imageName
                            ) {
                                return true;
                            }
                            const parentType = afterSmileTypeFromRestoredFileType(
                                rs.smileType
                            );
                            return (
                                parentType === after.smileType &&
                                restoredImageNameForAfter(after.imageName) ===
                                    rs.imageName
                            );
                        }) ?? null;

                    if (!matchedWf && !matchedOverlay && !matchedRestored) return after;

                    return {
                        ...after,
                        ...(matchedWf
                            ? {
                                  wireframe: {
                                      image: matchedWf.image,
                                      imageName: matchedWf.imageName,
                                      saved: true,
                                  },
                              }
                            : {}),
                        ...(matchedOverlay
                            ? {
                                  overlay: {
                                      image: matchedOverlay.image,
                                      imageName: matchedOverlay.imageName,
                                      saved: true,
                                  },
                              }
                            : {}),
                        ...(matchedRestored
                            ? {
                                  restored: {
                                      image: matchedRestored.image,
                                      imageName: matchedRestored.imageName,
                                      saved: true,
                                  },
                              }
                            : {}),
                    };
                });

                setHistory(validHistory);

                if (validHistory.length > 0) {
                    setSelectedImage(validHistory[0]);
                }

                const latestVideo = videoFiles.length
                    ? videoFiles[videoFiles.length - 1]
                    : null;
                if (latestVideo?.file_id) {
                    dispatch(
                        setCaseVideo({
                            boxFileId: Number(latestVideo.file_id),
                            fileName: latestVideo.file_name,
                        })
                    );
                } else {
                    dispatch(resetVideoState());
                }
            } catch (error) {
                console.error("Failed to load case data structural layout", error);
            } finally {
                if (!cancelled) setIsDataInitializing(false);
            }
        };

        loadCaseData();
        return () => {
            cancelled = true;
        };
    }, [selectedCase, isNewCase, boxAccessToken, dispatch, smile_id]);

    useEffect(() => {
        if (!result?.success || !result.data) return;
        if (isDataInitializing) return;

        const { image, smileType: rawSmileType } = result.data;
        if (!image) return;

        if (history.some((h) => h.image === image)) return;
        if (history.some((h) => h.wireframe?.image === image)) return;
        if (history.some((h) => h.overlay?.image === image)) return;
        if (history.some((h) => h.restored?.image === image)) return;

        const smileType = rawSmileType || "Custom Smile";

        // Stage 2 outline → attach wireframe to the source after, do not add as a design
        if (
            smileType === DENTAL_ACCURACY_OUTLINE_LABEL ||
            /outline|wireframe/i.test(smileType)
        ) {
            const sourceUrl =
                pendingWireframeSourceRef.current ?? selectedImage?.image ?? null;
            pendingWireframeSourceRef.current = null;
            if (!sourceUrl) return;

            const attach = (item: HistoryItem): HistoryItem => ({
                ...item,
                wireframe: {
                    image,
                    imageName: wireframeImageNameForAfter(item.imageName),
                    saved: false,
                },
            });

            setHistory((prev) => {
                const sourceIndex = prev.findIndex(
                    (h) => isAfterDesignItem(h) && h.image === sourceUrl
                );
                if (sourceIndex < 0) return prev;
                const next = [...prev];
                next[sourceIndex] = attach(prev[sourceIndex]);
                return next;
            });

            setSelectedImage((prev) =>
                prev && prev.image === sourceUrl ? attach(prev) : prev
            );
            return;
        }

        // Stage 2 extend → attach restored smile to the source after
        if (
            smileType === DENTAL_ACCURACY_EXTEND_LABEL ||
            smileType === DENTAL_ACCURACY_REFINE_LABEL
        ) {
            const sourceUrl =
                pendingExtendSourceRef.current ?? selectedImage?.image ?? null;
            pendingExtendSourceRef.current = null;
            if (!sourceUrl) return;

            const attach = (item: HistoryItem): HistoryItem => ({
                ...item,
                restored: {
                    image,
                    imageName: restoredImageNameForAfter(item.imageName),
                    saved: false,
                },
            });

            setHistory((prev) => {
                const sourceIndex = prev.findIndex(
                    (h) => isAfterDesignItem(h) && h.image === sourceUrl
                );
                if (sourceIndex < 0) return prev;
                const next = [...prev];
                next[sourceIndex] = attach(prev[sourceIndex]);
                return next;
            });

            setSelectedImage((prev) =>
                prev && prev.image === sourceUrl ? attach(prev) : prev
            );
            return;
        }

        const version =
            history.filter((h) => isAfterDesignItem(h) && h.smileType === smileType)
                .length + 1;

        const caseTag = smile_id || smileCase?.smile_id || patientName || "Patient";
        const imageName = `${caseTag}-${smileType}-V${version}-image.png`;
        const newItem: HistoryItem = {
            image,
            smileType,
            imageName,
            saved: false,
        };

        setHistory((prev) => [newItem, ...prev]);
        setSelectedImage(newItem);

        if (isSuperAdmin) {
            dispatch(fetchUser());
        }
    }, [result, patientName, history, isSuperAdmin, dispatch, selectedImage?.image, smile_id, smileCase?.smile_id, isDataInitializing]);

    const handleGenerate = useCallback(() => {
        if (!file) return;
        dispatch(
            generateSmile({
                user_id: String(user?.user_id) || "",
                file,
                customSmile: !useSelectPrompt ? customSmile : undefined,
                baseSmileId: useSelectPrompt ? baseSmileId : undefined,
                requestKey: smile_id || smileCase?.smile_id || "new",
            })
        );
    }, [dispatch, file, user, useSelectPrompt, customSmile, baseSmileId, smile_id, smileCase?.smile_id]);

    const handleSave = useCallback(async (): Promise<boolean> => {
        try {
            setSaveError(null);
            if (!file || !smileCase) return false;

            const hasUnsavedAfters = history.some(
                (item) => isAfterDesignItem(item) && !item.saved
            );
            const hasUnsavedAttachments = history.some(
                (item) => isAfterDesignItem(item) && attachmentUnsaved(item)
            );
            const shouldUploadInput = !inputSaved;

            const hasUnsavedVideo = !!caseVideoUrl && !videoSaved;

            if (
                !shouldUploadInput &&
                !hasUnsavedAfters &&
                !hasUnsavedAttachments &&
                !hasUnsavedVideo
            ) {
                return true;
            }

            setSavingCase(true);

            // Box CCG tokens last ~60m; long parallel gens can outlive the
            // token fetched on mount. Force refresh so save doesn't 401/CORS.
            const accessToken = await dispatch(
                fetchBoxAccessToken(true)
            ).unwrap();

            const uploadedFiles = await uploadSmileCaseFiles({
                file,
                history,
                patientName: smileCase.patient_name || "",
                caseBoxId: smileCase.caseBoxId,
                accessToken,
                uploadInput: shouldUploadInput,
            });

            if (hasUnsavedVideo && caseVideoUrl) {
                uploadedFiles.push(
                    await uploadSmileVideoToCase({
                        videoUrl: caseVideoUrl,
                        patientName: smileCase.patient_name || "",
                        caseBoxId: smileCase.caseBoxId,
                        accessToken,
                    })
                );
            }

            if (uploadedFiles.length > 0) {
                await dispatch(syncSmileImages({
                    smileId: smileCase.smile_id,
                    imageData: uploadedFiles
                })).unwrap();
            }

            const savedVideo = uploadedFiles.find(
                (entry) => isSmileVideoFileType(entry.type)
            );
            if (savedVideo) {
                dispatch(
                    markVideoSaved({
                        boxFileId: Number(savedVideo.box_id),
                        fileName: savedVideo.name,
                    })
                );
            }

            // Mark saved only after Box upload + DB sync both succeed
            if (shouldUploadInput) {
                setInputSaved(true);
            }
            setHistory((prev) =>
                prev.map((item) => {
                    if (!isAfterDesignItem(item)) return item;
                    return {
                        ...item,
                        saved: true,
                        wireframe: item.wireframe
                            ? { ...item.wireframe, saved: true }
                            : undefined,
                        overlay: item.overlay
                            ? { ...item.overlay, saved: true }
                            : undefined,
                        restored: item.restored
                            ? { ...item.restored, saved: true }
                            : undefined,
                    };
                })
            );
            setSelectedImage((prev) =>
                prev
                    ? {
                          ...prev,
                          saved: true,
                          wireframe: prev.wireframe
                              ? { ...prev.wireframe, saved: true }
                              : undefined,
                          overlay: prev.overlay
                              ? { ...prev.overlay, saved: true }
                              : undefined,
                          restored: prev.restored
                              ? { ...prev.restored, saved: true }
                              : undefined,
                      }
                    : prev
            );
            return true;
        } catch (error) {
            console.error(error);
            setSaveError(
                saveErrorMessage(
                    error,
                    "Failed to save smile images. Please try again."
                )
            );
            return false;
        } finally {
            setSavingCase(false);
        }
    }, [dispatch, file, smileCase, history, inputSaved, caseVideoUrl, videoSaved]);

    /**
     * Stage 2 — persist wireframes + edited overlays + restores mapped to their afters.
     * Also uploads unsaved parent afters so restores rematch after reload.
     */
    const handleSaveWireframes = useCallback(
        async (historyOverride?: HistoryItem[]): Promise<boolean> => {
            try {
                setSaveError(null);
                const source = historyOverride ?? history;
                const hasUnsaved = source.some(
                    (item) => isAfterDesignItem(item) && attachmentUnsaved(item)
                );
                if (!file || !smileCase) return false;
                if (!hasUnsaved) return true;

                setSavingCase(true);

                const accessToken = await dispatch(
                    fetchBoxAccessToken(true)
                ).unwrap();

                const uploadedFiles = await uploadSmileCaseFiles({
                    file,
                    history: source,
                    patientName: smileCase.patient_name || "",
                    caseBoxId: smileCase.caseBoxId,
                    accessToken,
                    uploadInput: false,
                    wireframesOnly: true,
                });

                if (uploadedFiles.length === 0) return false;

                await dispatch(
                    syncSmileImages({
                        smileId: smileCase.smile_id,
                        imageData: uploadedFiles,
                    })
                ).unwrap();

                const uploadedNames = new Set(
                    uploadedFiles.map((entry) => entry.name)
                );

                setHistory((prev) =>
                    prev.map((item) => {
                        if (!isAfterDesignItem(item)) return item;
                        return {
                            ...item,
                            saved:
                                item.saved || uploadedNames.has(item.imageName),
                            wireframe: item.wireframe
                                ? { ...item.wireframe, saved: true }
                                : undefined,
                            overlay: item.overlay
                                ? { ...item.overlay, saved: true }
                                : undefined,
                            restored: item.restored
                                ? { ...item.restored, saved: true }
                                : undefined,
                        };
                    })
                );
                setSelectedImage((prev) =>
                    prev
                        ? {
                              ...prev,
                              saved:
                                  prev.saved ||
                                  uploadedNames.has(prev.imageName),
                              wireframe: prev.wireframe
                                  ? { ...prev.wireframe, saved: true }
                                  : undefined,
                              overlay: prev.overlay
                                  ? { ...prev.overlay, saved: true }
                                  : undefined,
                              restored: prev.restored
                                  ? { ...prev.restored, saved: true }
                                  : undefined,
                          }
                        : prev
                );
                return true;
            } catch (error) {
                console.error(error);
                setSaveError(
                    saveErrorMessage(
                        error,
                        "Failed to save extended restore. Please try again."
                    )
                );
                return false;
            } finally {
                setSavingCase(false);
            }
        },
        [dispatch, file, smileCase, history]
    );

    /** Attach a wireframe traced from an after's teeth to that after. */
    const handleWireframeTraced = useCallback(
        (afterUrl: string, wireframeDataUrl: string) => {
            const attach = (item: HistoryItem): HistoryItem => ({
                ...item,
                wireframe: {
                    image: wireframeDataUrl,
                    imageName: wireframeImageNameForAfter(item.imageName),
                    saved: false,
                },
            });
            setHistory((prev) =>
                prev.map((item) =>
                    isAfterDesignItem(item) && item.image === afterUrl
                        ? attach(item)
                        : item
                )
            );
            setSelectedImage((prev) =>
                prev && prev.image === afterUrl ? attach(prev) : prev
            );
        },
        []
    );

    /** Attach edited wireframe-on-before to the selected after, then upload to Box. */
    const handleSaveEditedOverlay = useCallback(
        async (dataUrl: string): Promise<boolean> => {
            if (
                !selectedImage ||
                !isAfterDesignItem(selectedImage) ||
                !file ||
                !smileCase
            ) {
                return false;
            }

            const overlay = {
                image: dataUrl,
                imageName: overlayImageNameForAfter(selectedImage.imageName),
                saved: false as const,
            };

            const nextHistory = history.map((item) =>
                item.image === selectedImage.image
                    ? { ...item, overlay }
                    : item
            );

            setHistory(nextHistory);
            setSelectedImage({ ...selectedImage, overlay });
            return handleSaveWireframes(nextHistory);
        },
        [selectedImage, file, smileCase, history, handleSaveWireframes]
    );

    const askLabToken = !!user && user.role !== "User";

    const handleCreateCase = useCallback(async () => {
        try {
            setCreatingCase(true);
            setCreateCaseError(null);
            const trimmedToken = labToken.trim().toUpperCase();
            const res = await dispatch(
                createSmileCase({
                    patient_name: patientName,
                    user_id: user?.user_id || 0,
                    ...(askLabToken && trimmedToken.length === 2
                        ? { upload_token: trimmedToken }
                        : {}),
                })
            ).unwrap();

            if (res?.smile_id) {
                setSmileCase({
                    smile_id: res.smile_id,
                    patient_name: res.patient_name,
                    caseBoxId: res.box_folder_id,
                });
                setShowCaseModal(false);
            }
        } catch (error) {
            const message =
                typeof error === "string"
                    ? error
                    : "Could not create the smile case.";
            setCreateCaseError(message);
            console.error(error);
        } finally {
            setCreatingCase(false);
        }
    }, [dispatch, patientName, user, askLabToken, labToken]);

    const handleDownload = useCallback(async (url: string, filename: string) => {
        try {
            const response = await fetch(url);
            const blob = await response.blob();
            const blobUrl = window.URL.createObjectURL(blob);

            const link = document.createElement("a");
            link.href = blobUrl;
            link.download = filename;
            document.body.appendChild(link);
            link.click();

            document.body.removeChild(link);
            window.URL.revokeObjectURL(blobUrl);
        } catch (error) {
            console.error("Download failed", error);
        }
    }, []);

    const handleDownloadBefore = useCallback(async () => {
        if (!file && !preview) return;

        const filename = file?.name || `${patientName || "Patient"}-before-image.jpg`;

        if (file) {
            const blobUrl = window.URL.createObjectURL(file);
            const link = document.createElement("a");
            link.href = blobUrl;
            link.download = filename;
            document.body.appendChild(link);
            link.click();
            document.body.removeChild(link);
            window.URL.revokeObjectURL(blobUrl);
            return;
        }

        if (preview) {
            await handleDownload(preview, filename);
        }
    }, [file, preview, patientName, handleDownload]);

    const handleDownloadAfter = useCallback(async () => {
        if (!selectedImage?.image) return;
        const base =
            selectedImage.imageName ||
            `${patientName || "Patient"}-${selectedImage.smileType || "smile"}-after`;
        const filename = /\.(jpe?g|png|webp)$/i.test(base) ? base : `${base}.jpg`;
        await handleDownload(selectedImage.image, filename);
    }, [selectedImage, patientName, handleDownload]);

    const selectedImageIndex = useMemo(() => {
        return history.findIndex((item) => item.image === selectedImage?.image);
    }, [history, selectedImage]);

    const onFileSelect = useCallback((selectedFile: File, filePreview: string) => {
        setFile(selectedFile);
        setPreview(filePreview);
        // Replacing the before photo means it needs to be uploaded again
        setInputSaved(false);
    }, []);

    const handleSelectStl = useCallback((id: string) => {
        setBaseSmileId(id);
        if (!id || file || preview) return;
        window.setTimeout(() => {
            uploadStepRef.current?.scrollIntoView({
                behavior: "smooth",
                block: "start",
            });
        }, 80);
    }, [file, preview]);

    useEffect(() => {
        setHasUnsavedChanges(hasUnsavedChanges);
        return () => setHasUnsavedChanges(false);
    }, [hasUnsavedChanges, setHasUnsavedChanges]);

    useEffect(() => {
        registerSaveHandler(handleSave);
        return () => registerSaveHandler(null);
    }, [handleSave, registerSaveHandler]);

    useEffect(() => {
        if (user?.smile_credits != null) {
            dispatch(setRemainingCredits(user.smile_credits));
        }
    }, [dispatch, user]);

    const isLowCredits = !isSuperAdmin && typeof credits === "number" && credits <= 5;
    const isSystemActive = isSuperAdmin || (user?.smile_credits ?? 0) > 0;

    const hasAfterDesigns = history.some(isAfterDesignItem);
    const stlSelected = !!baseSmileId;
    const photoReady = !!file || !!preview;
    const uploadEnabled = stlSelected || photoReady;
    const generateEnabled =
        photoReady && stlSelected && !loading && !savingCase;
    const saveEnabled = hasAfterDesigns && canSave && !loading && !savingCase;
    const disableImageUpload = !isNewCase && !!file;
    const imageStepsComplete =
        photoReady &&
        hasAfterDesigns &&
        !canSave &&
        !loading &&
        !savingCase &&
        (stlSelected || !isNewCase);

    const canAccessStage2 = history.some(isAfterDesignItem);
    const stage2Completed = history.some(
        (item) =>
            !!item.wireframe?.image ||
            !!item.overlay?.image ||
            !!item.restored?.image
    );
    const videoFeatureEnabled = isSuperAdmin;
    const canAccessStage3 = videoFeatureEnabled ? canAccessStage2 : true;
    const upcomingWorkflowStages = useMemo(
        () => (videoFeatureEnabled ? new Set<WorkflowStage>() : new Set<WorkflowStage>([3])),
        [videoFeatureEnabled]
    );
    const hasUnsavedGeneratedImages = history.some(
        (item) =>
            isAfterDesignItem(item) &&
            (!item.saved || attachmentUnsaved(item))
    );
    const unsavedGeneratedCount = history.filter(
        (item) =>
            isAfterDesignItem(item) &&
            (!item.saved || attachmentUnsaved(item))
    ).length;

    const workflowCompletedStages = useMemo(() => {
        const done = new Set<WorkflowStage>();
        if (imageStepsComplete) done.add(1);
        if (stage2Completed) done.add(2);
        if (videoFeatureEnabled && videoReady) done.add(3);
        return done;
    }, [imageStepsComplete, stage2Completed, videoFeatureEnabled, videoReady]);

    const canAccessWorkflowStage = useCallback(
        (stage: WorkflowStage) => {
            if (stage === 1) return true;
            if (stage === 2) return canAccessStage2;
            if (stage === 3) return canAccessStage3;
            return false;
        },
        [canAccessStage2, canAccessStage3]
    );

    const navigateToStage = useCallback((stage: WorkflowStage) => {
        setCurrentStage(stage);
        setPendingStage(null);
    }, []);

    const requestStageNavigation = useCallback((stage: WorkflowStage) => {
        if (stage === 1) {
            setPendingStage(null);
            setCurrentStage(1);
            return;
        }

        if (!canAccessWorkflowStage(stage)) return;

        if (stage === 3 && !videoFeatureEnabled) {
            navigateToStage(3);
            return;
        }

        if (hasUnsavedGeneratedImages) {
            setPendingStage(stage);
            return;
        }

        navigateToStage(stage);
    }, [canAccessWorkflowStage, hasUnsavedGeneratedImages, navigateToStage, videoFeatureEnabled]);

    return (
        <>
            <Helmet><title>Smile Design Studio</title></Helmet>
            <div className="smile-studio -mx-1 sm:mx-0 pb-4">
                {showCreditsModal && (
                            <div className="fixed inset-0 z-[9999] flex items-center justify-center bg-black/70 backdrop-blur-md p-4">
                                <div className="w-full max-w-md rounded-2xl bg-[#1a1a1a] border border-red-500/20 p-5 sm:p-6 shadow-[0_0_40px_rgba(239,68,68,0.12)]">
                                    <div className="flex items-start gap-3 sm:gap-4 mb-4">
                                        <div className="shrink-0 w-11 h-11 rounded-xl bg-red-500/10 border border-red-500/20 flex items-center justify-center">
                                            <FiAlertCircle className="w-5 h-5 text-red-400" />
                                        </div>
                                        <div className="min-w-0">
                                            <h2 className="text-lg sm:text-xl font-bold text-red-400">
                                                Smile Credits Exhausted
                                            </h2>
                                            <p className="text-gray-400 text-sm mt-1">
                                                Recharge to continue generating designs
                                            </p>
                                        </div>
                                    </div>
                                    <p className="text-gray-300 text-sm leading-relaxed mb-6 sm:pl-[3.75rem]">
                                        {error}
                                    </p>
                                    <div className="flex justify-end">
                                        <button
                                            onClick={() => {
                                                setShowCreditsModal(false);
                                                window.location.reload();
                                            }}
                                            className="px-5 py-2.5 rounded-xl bg-[#2fa9bf] text-white font-medium hover:bg-[#36bfd8] transition-colors"
                                        >
                                            OK
                                        </button>
                                    </div>
                                </div>
                            </div>
                        )}

                        <UnsavedStageConfirmModal
                            isOpen={pendingStage !== null}
                            targetStage={pendingStage ?? 2}
                            unsavedCount={unsavedGeneratedCount}
                            onCancel={() => setPendingStage(null)}
                            onContinue={() => {
                                if (pendingStage) navigateToStage(pendingStage);
                            }}
                        />

                        {isNewCase && (
                            <CreateCaseModal
                                isOpen={showCaseModal && !creatingCase}
                                patientName={patientName}
                                setPatientName={setPatientName}
                                askLabToken={askLabToken}
                                labToken={labToken}
                                setLabToken={(value) => {
                                    setCreateCaseError(null);
                                    setLabToken(value);
                                }}
                                error={createCaseError}
                                onCreate={handleCreateCase}
                                onClose={() => {
                                    setShowCaseModal(false);
                                    if (window.customNavigate) {
                                        window.customNavigate("/dashboard/smiledesign/my-smiles");
                                    } else {
                                        navigate("/dashboard/smiledesign/my-smiles");
                                    }
                                }}
                            />
                        )}

                        <GenerationLoader
                            loading={
                                loading ||
                                creatingCase ||
                                savingCase ||
                                isFetchingCase ||
                                isDataInitializing
                            }
                            variant={
                                isFetchingCase || isDataInitializing
                                    ? "case-load"
                                    : creatingCase
                                      ? "case-create"
                                      : savingCase
                                        ? "case-save"
                                        : currentStage === 2
                                          ? "dental"
                                          : currentStage === 3
                                            ? "video"
                                            : "image"
                            }
                        />

                        <div className="relative flex flex-col gap-3 lg:grid lg:grid-cols-[1fr_auto_1fr] lg:items-center lg:gap-4 mb-4 sm:mb-[22px]">
                            <div className="min-w-0 pl-11 sm:pl-12 md:pl-0">
                                <h1 className="text-lg sm:text-[22px] font-semibold mb-1.5">Smile Design Studio</h1>
                                <div className="flex flex-wrap items-center gap-2.5">
                                    {smileCase?.smile_id && (
                                        <span className="inline-flex items-center px-[11px] py-[5px] rounded-full text-xs bg-[#212226] border border-white/[0.07] text-[#F1F0EA] ss-mono">
                                            {smileCase.smile_id}
                                        </span>
                                    )}
                                    {smileCase?.patient_name && (
                                        <span className="inline-flex items-center gap-1.5 px-[11px] py-[5px] rounded-full text-xs bg-[#212226] border border-white/[0.07] text-[#F1F0EA] font-medium">
                                            <span className="w-1.5 h-1.5 rounded-full bg-[#4ADE80]" />
                                            {smileCase.patient_name}
                                        </span>
                                    )}
                                    {hasUnsavedChanges && (
                                        <span className="inline-flex items-center gap-1.5 px-[11px] py-[5px] rounded-full text-xs bg-amber-500/10 text-amber-400 border border-amber-500/20">
                                            Unsaved changes
                                        </span>
                                    )}
                                </div>
                            </div>

                            <div className="flex items-center justify-center gap-2 sm:gap-2.5 min-w-0 flex-wrap pl-11 sm:pl-12 md:pl-0 lg:pl-0">
                                <WorkflowStageStepper
                                    currentStage={currentStage}
                                    onStageChange={requestStageNavigation}
                                    completedStages={workflowCompletedStages}
                                    canAccessStage={canAccessWorkflowStage}
                                    upcomingStages={upcomingWorkflowStages}
                                    highlightStage={
                                        currentStage === 1 &&
                                        imageStepsComplete &&
                                        canAccessStage2
                                            ? 2
                                            : null
                                    }
                                />
                                <button
                                    type="button"
                                    onClick={() => void handleSave()}
                                    disabled={loading || savingCase || !canSave}
                                    className="inline-flex items-center justify-center gap-1.5 shrink-0 h-[42px] px-3.5 sm:px-4 rounded-[11px] text-[13px] font-semibold border transition-all disabled:opacity-40 disabled:cursor-not-allowed bg-gradient-to-br from-[#1DA8C6] to-[#14879f] text-[#08151A] border-transparent hover:brightness-110"
                                >
                                    {savingCase ? (
                                        <>
                                            <span className="w-3.5 h-3.5 rounded-full border-2 border-[rgba(8,21,26,0.3)] border-t-[#08151A] animate-spin" />
                                            Saving…
                                        </>
                                    ) : (
                                        <>
                                            <FiSave className="w-[15px] h-[15px]" />
                                            Save
                                        </>
                                    )}
                                </button>
                            </div>

                            <div className="grid grid-cols-2 sm:flex sm:items-center sm:justify-end gap-2.5 w-full lg:w-auto pl-11 sm:pl-12 md:pl-0 lg:pl-0">
                                <div className="flex items-center gap-2.5 bg-[#212226] border border-white/[0.07] rounded-[11px] px-3.5 py-2">
                                    <div>
                                        <div className="text-[15px] font-semibold text-[#1DA8C6] font-[Poppins] leading-tight">
                                            {isSuperAdmin ? (credits ?? 0) : (credits ?? "—")}
                                        </div>
                                        <div className="text-sm text-[#6B6C72]">
                                            {isSuperAdmin ? "Smiles generated" : "Credits remaining"}
                                        </div>
                                    </div>
                                </div>

                                <div className="flex items-center gap-2.5 bg-[#212226] border border-white/[0.07] rounded-[11px] px-3.5 py-2">
                                    <div>
                                        <div className={`text-[15px] font-semibold font-[Poppins] leading-tight ${isSystemActive ? "text-[#4ADE80]" : "text-red-400"}`}>
                                            {isSuperAdmin ? "∞" : isSystemActive ? "Active" : "Stopped"}
                                        </div>
                                        <div className="text-sm text-[#6B6C72]">
                                            {isSuperAdmin ? "Unlimited access" : "Account status"}
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>

                        {!isSuperAdmin && isLowCredits && (
                            <div className="flex items-start gap-2.5 sm:gap-3 px-3 sm:px-4 py-2.5 sm:py-3 rounded-xl bg-red-500/5 border border-red-500/15">
                                <FiAlertCircle className="w-4 h-4 text-red-400 shrink-0 mt-0.5" />
                                <p className="text-xs sm:text-sm text-red-300/90 leading-relaxed">
                                    You are running low on credits. Recharge soon to keep generating smile designs.
                                </p>
                            </div>
                        )}

                        {saveError && (
                            <div className="flex items-start gap-2.5 sm:gap-3 px-3 sm:px-4 py-2.5 sm:py-3 rounded-xl bg-red-500/5 border border-red-500/15">
                                <FiAlertCircle className="w-4 h-4 text-red-400 shrink-0 mt-0.5" />
                                <p className="text-xs sm:text-sm text-red-300/90 leading-relaxed">
                                    {saveError}
                                </p>
                            </div>
                        )}

                        {error && !showCreditsModal && (
                            <div className="flex items-start gap-2.5 sm:gap-3 px-3 sm:px-4 py-2.5 sm:py-3 rounded-xl bg-red-500/5 border border-red-500/15">
                                <FiAlertCircle className="w-4 h-4 text-red-400 shrink-0 mt-0.5" />
                                <p className="text-xs sm:text-sm text-red-300/90 leading-relaxed">
                                    {error}
                                </p>
                            </div>
                        )}

                        {currentStage === 1 && (
                            <div className="flex flex-col gap-2">
                                <ImageFlowStep
                                    step={1}
                                    label="Select STL"
                                    status={stlSelected ? "done" : "active"}
                                >
                                    <SmileSelector
                                        value={baseSmileId}
                                        prompts={basePrompts}
                                        onChange={handleSelectStl}
                                    />
                                </ImageFlowStep>

                                <div className="grid grid-cols-1 items-start gap-4 lg:grid-cols-[minmax(0,420px)_minmax(0,1fr)] lg:gap-6">
                                <div className="flex min-w-0 flex-col">
                                <ImageFlowStep
                                    ref={uploadStepRef}
                                    step={2}
                                    label="Upload photo"
                                    status={
                                        !uploadEnabled
                                            ? "locked"
                                            : photoReady
                                              ? "done"
                                              : "active"
                                    }
                                >
                                    <FormCard title="Upload Image">
                                        <ImageInput
                                            onFileSelect={onFileSelect}
                                            preview={preview}
                                            disabled={!uploadEnabled || disableImageUpload}
                                            disabledHint={
                                                !uploadEnabled
                                                    ? "Select an STL to enable photo upload."
                                                    : undefined
                                            }
                                        />
                                    </FormCard>
                                </ImageFlowStep>

                                <ImageFlowStep
                                    step={3}
                                    label="Generate"
                                    status={
                                        hasAfterDesigns
                                            ? "done"
                                            : photoReady && stlSelected
                                              ? "active"
                                              : "locked"
                                    }
                                >
                                    <div className="flex">
                                        <ActionButton
                                            onClick={handleGenerate}
                                            loading={loading}
                                            disabled={!generateEnabled}
                                            text="Generate Smile"
                                            loadingText="Generating…"
                                            variant="primary"
                                        />
                                    </div>
                                </ImageFlowStep>

                                <ImageFlowStep
                                    step={4}
                                    label="Save"
                                    status={
                                        !hasAfterDesigns
                                            ? "locked"
                                            : saveEnabled
                                              ? "active"
                                              : "done"
                                    }
                                    isLast
                                >
                                    <div className="flex flex-col gap-2">
                                        <div className="flex">
                                            <ActionButton
                                                onClick={handleSave}
                                                loading={savingCase}
                                                disabled={!saveEnabled}
                                                text="Save Smiles"
                                                loadingText="Saving…"
                                                icon="save"
                                                variant={saveEnabled ? "primary" : "ghost"}
                                            />
                                        </div>
                                        {imageStepsComplete && (
                                            <button
                                                type="button"
                                                onClick={() =>
                                                    requestStageNavigation(2)
                                                }
                                                className="w-full flex items-center justify-center gap-2 px-3.5 py-3 rounded-[9px] text-[13.5px] font-semibold border-transparent bg-gradient-to-br from-[#1DA8C6] to-[#14879f] text-[#08151A] hover:brightness-110 active:scale-[0.98] transition-all"
                                            >
                                                Continue to Dental
                                                <FiChevronRight className="w-4 h-4" />
                                            </button>
                                        )}
                                        {!hasAfterDesigns && (
                                            <p className="text-center text-sm text-[#6B6C72]">
                                                Generate a smile to enable saving
                                            </p>
                                        )}
                                    </div>
                                </ImageFlowStep>
                                </div>

                                <div
                                    className={`min-w-0 lg:sticky lg:top-4 ${
                                        photoReady
                                            ? "flex flex-col gap-5"
                                            : "pointer-events-none opacity-45"
                                    }`}
                                >
                                    {photoReady ? (
                                        <>
                                            <BeforeAfterPreview
                                                preview={preview}
                                                imageUrl={selectedImage?.image}
                                                openFullscreen={() =>
                                                    setFullscreenMode(true)
                                                }
                                                onDownloadBefore={
                                                    handleDownloadBefore
                                                }
                                                onDownloadAfter={
                                                    handleDownloadAfter
                                                }
                                            />
                                            <HistorySection
                                                history={history}
                                                selectedImage={selectedImage}
                                                setSelectedImage={setSelectedImage}
                                                patientName={patientName}
                                                handleDownload={handleDownload}
                                            />
                                        </>
                                    ) : (
                                        <div className="rounded-[14px] border border-white/[0.07] bg-[#212226] px-4 py-8 sm:px-5">
                                            <h3 className="text-sm font-semibold text-[#F1F0EA]">
                                                Live Preview
                                            </h3>
                                            <p className="mt-1 text-sm leading-relaxed text-[#6B6C72]">
                                                Upload a patient photo to enable the live preview and generate.
                                            </p>
                                        </div>
                                    )}
                                </div>
                                </div>
                            </div>
                        )}

                        {currentStage === 2 && (
                            <>
                            <DentalAccuracyStage
                                imageUrl={
                                    selectedImage && isAfterDesignItem(selectedImage)
                                        ? selectedImage.image
                                        : null
                                }
                                smileType={
                                    selectedImage && isAfterDesignItem(selectedImage)
                                        ? selectedImage.smileType
                                        : undefined
                                }
                                patientName={patientName}
                                smileId={smile_id || smileCase?.smile_id}
                                beforePreview={preview}
                                beforeFile={file}
                                wireframeUrl={selectedImage?.wireframe?.image ?? null}
                                wireframeSaved={!!selectedImage?.wireframe?.saved}
                                overlayUrl={selectedImage?.overlay?.image ?? null}
                                overlaySaved={!!selectedImage?.overlay?.saved}
                                restoredUrl={selectedImage?.restored?.image ?? null}
                                restoredSaved={!!selectedImage?.restored?.saved}
                                onComplete={() => {}}
                                onWireframeTraced={handleWireframeTraced}
                                onExtendGenerateStart={(afterUrl) => {
                                    pendingExtendSourceRef.current = afterUrl;
                                }}
                                onSave={() => void handleSaveWireframes()}
                                onSaveEditedOverlay={handleSaveEditedOverlay}
                                onLiveEditedAfter={handleLiveEditedAfter}
                                saving={savingCase}
                                canSave={
                                    !!file && !!smileCase && hasUnsavedWireframes
                                }
                                canSaveOverlay={!!file && !!smileCase}
                                selectedSaved={
                                    (!!selectedImage?.wireframe ||
                                        !!selectedImage?.overlay ||
                                        !!selectedImage?.restored) &&
                                    (!selectedImage?.wireframe ||
                                        !!selectedImage.wireframe.saved) &&
                                    (!selectedImage?.overlay ||
                                        !!selectedImage.overlay.saved) &&
                                    (!selectedImage?.restored ||
                                        !!selectedImage.restored.saved)
                                }
                            />
                            {canOrderCaseFromSmile && (
                            <SmileCaseOrderSection
                                smileId={smileCase?.smile_id}
                                patientName={patientName}
                                beforePreview={preview}
                                beforeFile={file}
                                history={history}
                                liveEditedAfterUrl={liveEditedAfter}
                                createdCase={portalCase}
                                linkedCaseId={selectedCase?.caseDetails?.case_id || null}
                                alreadyLinked={!!selectedCase?.caseDetails?.case_id}
                                isSmileCaseLoading={!isNewCase && isFetchingCase}
                                onCaseCreated={setPortalCase}
                            />
                            )}
                            </>
                        )}

                        {currentStage === 3 &&
                            (videoFeatureEnabled ? (
                            <VideoGenerationStage
                                beforeImage={preview}
                                afterImage={
                                    selectedImage && isAfterDesignItem(selectedImage)
                                        ? selectedImage.image
                                        : null
                                }
                                afterImageName={selectedImage?.imageName}
                                smileType={selectedImage?.smileType}
                                patientName={patientName}
                                smileId={smileCase?.smile_id}
                                caseBoxId={smileCase?.caseBoxId}
                                history={history.filter(isAfterDesignItem)}
                                selectedImage={
                                    selectedImage && isAfterDesignItem(selectedImage)
                                        ? selectedImage
                                        : null
                                }
                                onSelectAfter={setSelectedImage}
                                onComplete={() => {}}
                            />
                            ) : (
                                <UpcomingVideoStage />
                            ))}

                {fullscreenMode && (
                    <FullscreenViewer
                        beforeImage={preview}
                        history={history}
                        selectedIndex={selectedImageIndex}
                        onImageChange={(item) => setSelectedImage(item)}
                        onClose={() => setFullscreenMode(false)}
                    />
                )}
            </div>
        </>
    );
}














// const SmileGeneration = () => {
//   return (
//     <div className="min-h-screen flex items-center justify-center bg-gray-100 px-4">
//       <div className="max-w-md w-full bg-white rounded-2xl shadow-xl p-10 text-center">
//         <div className="text-6xl mb-4">🛠️</div>

//         <h1 className="text-3xl font-bold text-gray-800 mb-3">
//           Under Maintenance
//         </h1>

//         <p className="text-gray-600 leading-relaxed">
//           We're currently performing scheduled maintenance to improve your
//           experience.
//         </p>

//         <p className="text-gray-500 mt-2">
//           Please check back again shortly.
//         </p>

//         <button
//           onClick={() => window.location.reload()}
//           className="mt-8 bg-blue-600 hover:bg-blue-700 text-white px-6 py-3 rounded-lg transition duration-200"
//         >
//           Refresh Page
//         </button>
//       </div>
//     </div>
//   );
// };

// export default SmileGeneration;